import type { LeagueWeekBetScoringOverride } from "./seasons";

export type LegacyScoringSystem = "oddsBased" | "totalWins";
export type BetSide =
  | "one"
  | "two"
  | "three"
  | "four"
  | "five"
  | "six"
  | "seven"
  | "eight"
  | "nine"
  | "ten"
  | "eleven";
export type SeasonScoringConfig = {
  legacyScoringSystem?: LegacyScoringSystem | string | null;
} | null;

export interface GameOddsInput {
  homeOdds: number | string | null | undefined;
  awayOdds: number | string | null | undefined;
}

export interface GameScoreInput {
  homeTeamId?: string | null;
  awayTeamId?: string | null;
  homeTeam?: { id: string } | null;
  awayTeam?: { id: string } | null;
  homeScore: number | null | undefined;
  awayScore: number | null | undefined;
  winnerId: string | null | undefined;
  isCompleted?: boolean | null;
}

export interface ScoringBetInput {
  betType?: "moneyline" | "spread" | "total" | "total_tiebreaker" | "custom_prop";
  oddsOne?: number | string | null;
  oddsTwo?: number | string | null;
  displayInfo?: Record<string, unknown> | null;
  scoringConfigOverride?: LeagueWeekBetScoringOverride | Record<string, unknown> | null;
}

export interface ScoringPickInput {
  selectedTeamId: string;
  side: BetSide;
}

/** Whether a bet contributes to the season and weekly standings for a season mode. */
export const getBetInclusion = (
  betType: ScoringBetInput["betType"],
  scoringConfig?: SeasonScoringConfig & { seasonWeekMode?: string },
): { weekly: boolean; season: boolean } => {
  if (betType === "total_tiebreaker") return { weekly: false, season: false };
  if (scoringConfig?.seasonWeekMode !== "propWeeklyOnly") return { weekly: true, season: true };
  const seasonOnly = betType === "moneyline" || betType === "spread";
  return { weekly: !seasonOnly, season: seasonOnly };
};

/** Select positive weekly leaders by score, then tiebreaker closeness when available. */
export const selectWeeklyWinnerUserIds = <T extends { userId: string; weekPoints: number; tiebreakerDelta?: number }>(
  standings: T[],
): string[] => {
  const positive = standings.filter((row) => Number.isFinite(row.weekPoints) && row.weekPoints > 0);
  if (!positive.length) return [];
  const topPoints = Math.max(...positive.map((row) => row.weekPoints));
  const topScores = positive.filter((row) => row.weekPoints === topPoints);
  if (topScores.length < 2) return topScores.map((row) => row.userId);

  const deltaFor = (row: T) => typeof row.tiebreakerDelta === "number"
    && Number.isFinite(row.tiebreakerDelta)
    && row.tiebreakerDelta >= 0
    ? row.tiebreakerDelta
    : Number.POSITIVE_INFINITY;
  const closestDelta = Math.min(...topScores.map(deltaFor));
  return topScores.filter((row) => deltaFor(row) === closestDelta).map((row) => row.userId);
};

const parseOddsValue = (value?: number | string | null): number | null => {
  if (value === undefined || value === null) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseDisplayNumber = (value?: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const resolveLegacyScoringSystem = (
  scoringSystem?: LegacyScoringSystem | null,
  scoringConfig?: SeasonScoringConfig,
): LegacyScoringSystem => {
  const legacy = scoringConfig?.legacyScoringSystem;
  if (legacy === "oddsBased" || legacy === "totalWins") {
    return legacy;
  }
  if (scoringSystem === "oddsBased" || scoringSystem === "totalWins") {
    return scoringSystem;
  }
  return "oddsBased";
};

const resolveTeamIds = (
  game: Pick<GameScoreInput, "homeTeamId" | "awayTeamId" | "homeTeam" | "awayTeam">,
): { homeId: string; awayId: string } | null => {
  const homeId = game.homeTeamId ?? game.homeTeam?.id ?? null;
  const awayId = game.awayTeamId ?? game.awayTeam?.id ?? null;
  if (!homeId || !awayId) {
    return null;
  }
  return { homeId, awayId };
};

const normalizeOverride = (
  override: ScoringBetInput["scoringConfigOverride"],
): LeagueWeekBetScoringOverride | null => {
  if (!override || typeof override !== "object") {
    return null;
  }

  const type = "type" in override ? override.type : undefined;
  if (type === "oddsBased") {
    const multiplier =
      "multiplier" in override && typeof override.multiplier === "string"
        ? Number.parseFloat(override.multiplier)
        : "multiplier" in override && typeof override.multiplier === "number"
          ? override.multiplier
          : undefined;
    const normalizedMultiplier = Number.isFinite(multiplier) ? multiplier : undefined;
    return {
      type: "oddsBased",
      multiplier: normalizedMultiplier ?? 1,
    };
  }

  if (type === "nPoints") {
    const getVal = (key: string, legacyKey?: string) => {
      const val =
        key in override && typeof (override as any)[key] === "string"
          ? Number.parseFloat((override as any)[key])
          : key in override && typeof (override as any)[key] === "number"
            ? (override as any)[key]
            : legacyKey && legacyKey in override && typeof (override as any)[legacyKey] === "string"
              ? Number.parseFloat((override as any)[legacyKey])
              : legacyKey && legacyKey in override && typeof (override as any)[legacyKey] === "number"
                ? (override as any)[legacyKey]
                : undefined;
      return isFiniteNumber(val) ? val : undefined;
    };

    const one = getVal("one", "side1");
    const two = getVal("two", "side2");

    if (one === undefined || two === undefined) {
      return null;
    }

    const result: any = {
      type: "nPoints",
      one,
      two,
      side1: one,
      side2: two,
    };

    const extras = [
      ["three", "side3"],
      ["four", "side4"],
      ["five", "side5"],
      ["six", "side6"],
      ["seven", "side7"],
      ["eight", "side8"],
      ["nine", "side9"],
      ["ten", "side10"],
      ["eleven", "side11"],
    ] as const;

    for (const [key, legacyKey] of extras) {
      const val = getVal(key, legacyKey);
      if (val !== undefined) {
        result[key] = val;
      }
    }

    return result as LeagueWeekBetScoringOverride;
  }

  return null;
};

export const calculatePointsFromOdds = (odds: number): number => {
  if (!Number.isFinite(odds)) {
    return 0;
  }
  const probability = odds > 0 ? 100 / (odds + 100) : Math.abs(odds) / (Math.abs(odds) + 100);
  if (probability === 0) return 0;
  return 10 / probability;
};

const resolveOddsForSide = (
  bet: ScoringBetInput,
  side: BetSide,
  fallback?: GameOddsInput,
): number | null => {
  if (side === "one") {
    if (bet.oddsOne !== undefined && bet.oddsOne !== null) return parseOddsValue(bet.oddsOne);
    if (fallback?.homeOdds !== undefined && fallback?.homeOdds !== null)
      return parseOddsValue(fallback.homeOdds);
  }
  if (side === "two") {
    if (bet.oddsTwo !== undefined && bet.oddsTwo !== null) return parseOddsValue(bet.oddsTwo);
    if (fallback?.awayOdds !== undefined && fallback?.awayOdds !== null)
      return parseOddsValue(fallback.awayOdds);
  }

  // Check displayInfo options for other sides (or if one/two were missing but might be in options)
  if (bet.displayInfo?.["options"] && Array.isArray(bet.displayInfo["options"])) {
    const option = bet.displayInfo["options"].find((o: any) => o.side === side);
    if (option && typeof option.odds === "number") {
      return option.odds;
    }
  }

  return null;
};

export const getBetPoints = ({
  scoringSystem,
  scoringConfig,
  bet,
  side,
  gameOdds,
}: {
  scoringSystem?: LegacyScoringSystem | null;
  scoringConfig?: SeasonScoringConfig;
  bet: ScoringBetInput;
  side: BetSide;
  gameOdds?: GameOddsInput;
}): number => {
  if (bet.betType === "total_tiebreaker") {
    return 0;
  }
  const override = normalizeOverride(bet.scoringConfigOverride);
  if (override?.type === "nPoints") {
    const points = (override as any)[side];
    if (typeof points === "number") return points;
    // Fallback for legacy side1/side2 keys if the side is 'one' or 'two'
    if (side === "one" && typeof override.side1 === "number") return override.side1!;
    if (side === "two" && typeof override.side2 === "number") return override.side2!;
    return 0;
  }

  const baseSystem = resolveLegacyScoringSystem(scoringSystem, scoringConfig);
  const effectiveSystem = override?.type === "oddsBased" ? "oddsBased" : baseSystem;
  if (effectiveSystem === "totalWins") {
    return 1;
  }

  const odds = resolveOddsForSide(bet, side, gameOdds);
  const basePoints = odds === null ? 1 : calculatePointsFromOdds(odds);
  const multiplier = override?.type === "oddsBased" ? override.multiplier ?? 1 : 1;
  return basePoints * multiplier;
};

export const formatPointsLabel = (
  points: number,
  scoringSystem?: LegacyScoringSystem | null,
  scoringConfig?: SeasonScoringConfig,
): string => {
  const resolvedSystem = resolveLegacyScoringSystem(scoringSystem, scoringConfig);
  if (resolvedSystem === "totalWins") {
    return `${Math.round(points)} pt${Math.round(points) === 1 ? "" : "s"}`;
  }
  const rounded = Math.abs(points - Math.round(points)) < 0.05 ? Math.round(points) : points;
  return `${rounded.toFixed(rounded % 1 === 0 ? 0 : 1)} pts`;
};

export const inferBetSideFromSelection = (
  game: Pick<GameScoreInput, "homeTeamId" | "awayTeamId" | "homeTeam" | "awayTeam">,
  selectedTeamId?: string | null,
): BetSide | null => {
  if (!selectedTeamId) return null;
  const ids = resolveTeamIds(game);
  if (!ids) return null;
  if (selectedTeamId === ids.homeId) return "one";
  if (selectedTeamId === ids.awayId) return "two";
  return null;
};

export const resolveWinningSide = (
  bet: ScoringBetInput,
  game: GameScoreInput,
): BetSide | "push" | null => {
  const homeScore = typeof game.homeScore === "number" ? game.homeScore : null;
  const awayScore = typeof game.awayScore === "number" ? game.awayScore : null;

  switch (bet.betType) {
    case "spread": {
      const spreadValue = parseDisplayNumber(bet.displayInfo?.["spread"]);
      if (spreadValue === null || homeScore === null || awayScore === null) {
        return null;
      }
      const diff = homeScore + spreadValue - awayScore;
      if (diff > 0) return "one";
      if (diff < 0) return "two";
      return "push";
    }
    case "total": {
      const totalValue = parseDisplayNumber(bet.displayInfo?.["total"]);
      if (totalValue === null || homeScore === null || awayScore === null) {
        return null;
      }
      const combined = homeScore + awayScore;
      if (combined > totalValue) return "one";
      if (combined < totalValue) return "two";
      return "push";
    }
    case "custom_prop": {
      if (!bet.displayInfo || typeof bet.displayInfo !== "object") {
        return null;
      }
      const winningSide = (bet.displayInfo as Record<string, unknown>)["winningSide"];
      if (typeof winningSide === "string" && winningSide) {
        if (winningSide === "push") return "push";
        return winningSide as BetSide;
      }
      return null;
    }
    case "total_tiebreaker": {
      return null;
    }
    default: {
      const ids = resolveTeamIds(game);
      if (!ids || !game.winnerId) return null;
      if (game.winnerId === ids.homeId) return "one";
      if (game.winnerId === ids.awayId) return "two";
      return null;
    }
  }
};

export const getBetLiveState = (
  bet: ScoringBetInput,
  game: GameScoreInput,
  side: BetSide,
): "leading" | "trailing" | "tied" => {
  const homeScore = typeof game.homeScore === "number" ? game.homeScore : null;
  const awayScore = typeof game.awayScore === "number" ? game.awayScore : null;

  if (homeScore === null || awayScore === null) {
    return "tied";
  }

  switch (bet.betType) {
    case "spread": {
      const spreadValue = parseDisplayNumber(bet.displayInfo?.["spread"]);
      if (spreadValue === null) return "tied";
      const diff = homeScore + spreadValue - awayScore;
      if (diff === 0) return "tied";
      const leader = diff > 0 ? "one" : "two";
      return leader === side ? "leading" : "trailing";
    }
    case "total": {
      const totalValue = parseDisplayNumber(bet.displayInfo?.["total"]);
      if (totalValue === null) return "tied";
      const combined = homeScore + awayScore;
      if (combined === totalValue) return "tied";
      const leader = combined > totalValue ? "one" : "two";
      return leader === side ? "leading" : "trailing";
    }
    case "custom_prop": {
      return "tied";
    }
    case "total_tiebreaker": {
      return "tied";
    }
    default: {
      if (homeScore === awayScore) return "tied";
      const leader = homeScore > awayScore ? "one" : "two";
      return leader === side ? "leading" : "trailing";
    }
  }
};

const resolveOddsForPick = (
  pick: ScoringPickInput,
  game: GameOddsInput,
  bet: ScoringBetInput,
): number | null => {
  if (bet.displayInfo?.["options"] && Array.isArray(bet.displayInfo["options"])) {
    const option = bet.displayInfo["options"].find((entry: any) => entry.side === pick.side);
    if (option && typeof option.odds === "number") {
      return option.odds;
    }
  }

  const betOddsRaw = pick.side === "one" ? bet.oddsOne ?? null : bet.oddsTwo ?? null;
  const betOdds = parseOddsValue(betOddsRaw);
  if (betOdds !== null) {
    return betOdds;
  }
  const fallbackOddsRaw = pick.side === "one" ? game.homeOdds ?? null : game.awayOdds ?? null;
  return parseOddsValue(fallbackOddsRaw);
};

const determinePickOutcome = (
  pick: ScoringPickInput,
  game: GameScoreInput,
  bet: ScoringBetInput,
): "win" | "loss" | "push" | null => {
  const homeScore = typeof game.homeScore === "number" ? game.homeScore : null;
  const awayScore = typeof game.awayScore === "number" ? game.awayScore : null;

  switch (bet.betType) {
    case "spread": {
      if (homeScore === null || awayScore === null) return null;
      const spreadValue = parseDisplayNumber(bet.displayInfo?.["spread"]);
      if (spreadValue === null) return null;
      const margin =
        pick.side === "one"
          ? homeScore + spreadValue - awayScore
          : awayScore - spreadValue - homeScore;
      if (margin > 0) return "win";
      if (margin < 0) return "loss";
      return "push";
    }
    case "total": {
      if (homeScore === null || awayScore === null) return null;
      const target = parseDisplayNumber(bet.displayInfo?.["total"]);
      if (target === null) return null;
      const combined = homeScore + awayScore;
      if (pick.side === "one") {
        if (combined > target) return "win";
        if (combined < target) return "loss";
      } else {
        if (combined < target) return "win";
        if (combined > target) return "loss";
      }
      return "push";
    }
    case "custom_prop": {
      if (!bet.displayInfo || typeof bet.displayInfo !== "object") {
        return null;
      }
      const winningSide = (bet.displayInfo as Record<string, unknown>)["winningSide"];
      if (typeof winningSide === "string" && winningSide) {
        if (winningSide === "push") return "push";
        return winningSide === pick.side ? "win" : "loss";
      }
      return null;
    }
    case "total_tiebreaker": {
      return null;
    }
    default: {
      const ids = resolveTeamIds(game);
      if (!ids || !game.winnerId) return null;
      return pick.selectedTeamId === game.winnerId ? "win" : "loss";
    }
  }
};

export const isPickCorrect = (
  pick: ScoringPickInput,
  game: GameScoreInput,
  bet: ScoringBetInput,
): boolean | null => {
  const outcome = determinePickOutcome(pick, game, bet);
  if (outcome === "win") return true;
  if (outcome === "loss") return false;
  return null;
};

export const calculatePickPoints = (
  pick: ScoringPickInput,
  game: GameScoreInput & GameOddsInput,
  bet: ScoringBetInput,
  options?: {
    scoringSystem?: LegacyScoringSystem | null;
    scoringConfig?: SeasonScoringConfig;
  },
): number => {
  const outcome = determinePickOutcome(pick, game, bet);
  if (outcome !== "win") {
    return 0;
  }
  if (bet.betType !== "custom_prop" && !game.isCompleted) {
    return 0;
  }

  const scoringOverride = normalizeOverride(bet.scoringConfigOverride);
  if (scoringOverride?.type === "nPoints") {
     const points = (scoringOverride as any)[pick.side];
     if (typeof points === 'number') return points;
     // Legacy fallbacks
     if (pick.side === "one" && typeof scoringOverride.side1 === "number") return scoringOverride.side1;
     if (pick.side === "two" && typeof scoringOverride.side2 === "number") return scoringOverride.side2;
     return 0;
  }

  const baseSystem = resolveLegacyScoringSystem(options?.scoringSystem, options?.scoringConfig);
  const effectiveSystem = scoringOverride?.type === "oddsBased" ? "oddsBased" : baseSystem;
  if (effectiveSystem === "totalWins") {
    return 1;
  }

  const odds = resolveOddsForPick(pick, game, bet);
  if (odds === null) {
    const basePoints = 2.0;
    if (scoringOverride?.type === "oddsBased") {
      return basePoints * (scoringOverride.multiplier ?? 1);
    }
    return basePoints;
  }
  const basePoints = calculatePointsFromOdds(odds);
  if (scoringOverride?.type === "oddsBased") {
    return basePoints * (scoringOverride.multiplier ?? 1);
  }
  return basePoints;
};
